/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { Alert, Button, Card, Select, SvgIcon, Table } from "core/components";
import {
  material_return_items_admin_material_return_path,
  admin_material_returns_path,
  receive_admin_material_return_path,
  print_admin_material_return_path
} from "routes";
import { navigateTo } from "core/navigation";
import { api, ApiError } from "core/api";
import { MaterialReturnStatus, ReturnsSchema } from "core/schema";
import { Controller, useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { yupResolver } from "@hookform/resolvers/yup";

const MaterialReturn = ({ material_return, states }): JSX.Element => {
  const [loading, setLoading] = React.useState(false);
  const [materialReturnItems, setMaterialReturnItems] = React.useState([]);
  const [showReceiveConfirmationModal, setShowReceiveConfirmationModal] = React.useState(false);
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [modalErrors, setModalErrors] = React.useState<Array<string>>([]);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(ReturnsSchema)
  });
  const table = React.useRef(null);
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Inventory ID",
        accessor: "item.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Inventory",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure.code",
        Cell: (props) => {
          const isOpen = material_return.status == MaterialReturnStatus.Open;

          if (isOpen) {
            setValue(`returns.${props.row.index}.id`, props.row.original.id);
            setValue(`returns.${props.row.index}.item_id`, props.row.original.item.id);
            setValue(`returns.${props.row.index}.warehouse_id`, material_return.warehouse.id);
            setValue(
              `returns.${props.row.index}.project_task_item_id`,
              props.row.original.project_task_item_id || props.row.original.change_order_task_item_id
            );
          }

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Returned Quantity",
        accessor: "returned_quantity",
        Cell: (props) => {
          const isOpen = material_return.status == MaterialReturnStatus.Open;
          const actualQuantity = parseFloat(props.row.original.actual_quantity);
          const openQuantity = parseFloat(props.row.original.open_return_quantity);
          const remainingQuantity = actualQuantity - openQuantity;

          if (isOpen) {
            return (
              <Controller
                name={`returns.${props.row.index}.returned_quantity`}
                control={control}
                render={({ field, fieldState }) => (
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString() || props.value}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      field.onChange(value);
                    }}
                    className="field"
                    placeholder={"0.0"}
                    min={0.1}
                    max={remainingQuantity > 0 ? remainingQuantity + parseFloat(props.value) : parseFloat(props.value)}
                    style={fieldState.error ? { borderColor: "red" } : {}}
                  />
                )}
              />
            );
          }

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "State",
        accessor: "state",
        Cell: (props) => {
          const isOpen = material_return.status == MaterialReturnStatus.Open;
          const state = I18n.t(`activerecord.attributes.material_return_item.states.${props.value}`);

          if (isOpen) {
            return (
              <Controller
                name={`returns.${props.row.index}.state`}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select State"
                    isClearable={false}
                    options={states}
                    noOptionsMessage={() => "No states available."}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    onChange={(newOption) => {
                      field.onChange(newOption?.value);
                    }}
                    value={states.find((state) => state.value == field.value) || setValue(`returns.${props.row.index}.state`, props.value)}
                    errorMessage={props.errors?.returns?.[props.row.index]?.state?.message}
                  />
                )}
              />
            );
          }

          return (
            <div title={state} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{state}</p>
            </div>
          );
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      }
    ],
    []
  );

  const getMaterialReturnItems = async () => {
    try {
      setLoading(true);
      const response = await api.get(material_return_items_admin_material_return_path(material_return.id));
      setMaterialReturnItems(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
    }
  };

  const receiveMaterialReturn = async (id, data) => {
    try {
      setLoading(true);
      const returns = materialReturnItems.map((materialReturnItem) => {
        const returnItem = data.returns.find((item) => item.id == materialReturnItem.id);

        if (returnItem) {
          return {
            id: returnItem.id,
            item_id: returnItem.item_id,
            project_task_item_id: returnItem.project_task_item_id,
            returned_quantity: returnItem.returned_quantity,
            state: returnItem.state,
            warehouse_id: material_return.warehouse.id
          };
        } else {
          return {
            id: materialReturnItem.id,
            item_id: materialReturnItem.item.id,
            project_task_item_id: materialReturnItem.project_task_item_id,
            returned_quantity: materialReturnItem.returned_quantity,
            state: materialReturnItem.state,
            warehouse_id: material_return.warehouse.id
          };
        }
      });
      const inventoryReturns = {
        ...data,
        returns
      };
      await api.put(receive_admin_material_return_path(id), inventoryReturns);
      window.open(print_admin_material_return_path(material_return.id));
      navigateTo(admin_material_returns_path());
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.all().find((e) => e.includes("already closed") || e.includes("cancelled"))) {
          setModalErrors(error.all());
          setShowErrorModal(!showErrorModal);
        } else setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
      setShowReceiveConfirmationModal(!showReceiveConfirmationModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  React.useEffect(() => {
    getMaterialReturnItems().catch(console.error);
    setValue("project_id", material_return.project.id);
  }, []);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading Material Return...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No material return items.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={materialReturnItems} ref={table} errors={errors} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return materialReturnItems.length > 0 ? <>{dataTable()}</> : noContent();
  };

  const canReceiveMaterialReturn = (materialReturn) => {
    return materialReturn.status == MaterialReturnStatus.Open;
  };

  const canPrint = () => {
    return material_return.status == MaterialReturnStatus.Closed;
  };

  return (
    <React.StrictMode>
      {formErrors && formErrors.length != 0 && (
        <Alert>
          {formErrors.map((error, index) => {
            return <div key={index}>{error}</div>;
          })}
        </Alert>
      )}
      <Card>
        <Card.Header title="Material Return Items">
          <Button
            title="Back"
            className="button--default"
            icon="arrow-left"
            onClick={() => {
              navigateTo(admin_material_returns_path());
            }}
          />
          {canPrint() && (
            <Button
              className="button--primary ml-3"
              icon="printer"
              title="Print"
              type="button"
              onClick={() => window.open(print_admin_material_return_path(material_return.id))}
            />
          )}
          {canReceiveMaterialReturn(material_return) && (
            <Button
              title="Receive"
              className="button--brand ml-3"
              icon="check-circle"
              loading={loading}
              disabled={loading}
              onClick={() => {
                setShowReceiveConfirmationModal(!showReceiveConfirmationModal);
              }}
            />
          )}
        </Card.Header>
        <Card.Body>{loading ? loader() : content()}</Card.Body>
      </Card>
      <Modal dialogClassName="modal-dialog-centered" show={showReceiveConfirmationModal}>
        <Modal.Header>{`Receive Material Return: ${material_return?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to receive Material Return from Project: ${material_return?.project?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowReceiveConfirmationModal(!showReceiveConfirmationModal);
            }}
            disabled={loading}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--success"
            icon="save"
            title="Yes, receive material return."
            loading={loading}
            disabled={loading}
            onClick={() => {
              handleSubmit(async (data) => await receiveMaterialReturn(material_return?.id, data), onError)();
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showErrorModal}>
        <Modal.Header>Error encountered</Modal.Header>
        <Modal.Body>
          {modalErrors && modalErrors.length != 0 && (
            <Alert>
              {modalErrors.map((error, index) => {
                return <div key={index}>{error}</div>;
              })}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowErrorModal(!showErrorModal)} icon="times" title="Close" />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default MaterialReturn;
